import { useMemo, useState } from "react";
import {
  X,
  Loader2,
  Star,
  ArrowLeftRight,
  AlertTriangle,
  History,
  Search,
} from "lucide-react";
import { toast } from "react-toastify";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  useAdminTeamDetail,
  useAdminTeamAudit,
  useAdminSwapPlayer,
  useAdminChangeCaptain,
  type AdminTeamPlayerCard,
  type AdminEligiblePlayer,
  type Position,
} from "@/hooks/api/useAdminTeams";
import { cn } from "@/lib/utils";

const POSITION_ROWS: { key: Position; label: string }[] = [
  { key: "FWD", label: "Forwards" },
  { key: "MID", label: "Midfielders" },
  { key: "DEF", label: "Defenders" },
  { key: "GK", label: "Goalkeeper" },
];

const STATUS_COLOR: Record<string, string> = {
  UPCOMING: "bg-gray-100 text-gray-700",
  OPEN: "bg-green-100 text-green-700",
  LOCKED: "bg-orange-100 text-orange-700",
  COMPLETED: "bg-blue-100 text-blue-700",
};

const POS_BG: Record<Position, string> = {
  GK: "bg-yellow-50 border-yellow-200",
  DEF: "bg-blue-50 border-blue-200",
  MID: "bg-emerald-50 border-emerald-200",
  FWD: "bg-rose-50 border-rose-200",
};

interface Props {
  teamId: number;
  onClose: () => void;
}

export default function AdminTeamDetailDrawer({ teamId, onClose }: Props) {
  const { data, isLoading } = useAdminTeamDetail(teamId);
  const { data: auditData } = useAdminTeamAudit(teamId);

  const [swapSlot, setSwapSlot] = useState<AdminTeamPlayerCard | null>(null);
  const [pendingAction, setPendingAction] = useState<
    | { kind: "swap"; outPlayerId: number; inPlayerId: number; inName: string }
    | { kind: "captain"; captainPlayerId: number; name: string }
    | null
  >(null);
  const [showAudit, setShowAudit] = useState(false);

  const swapMutation = useAdminSwapPlayer();
  const captainMutation = useAdminChangeCaptain();
  const isMutating = swapMutation.isPending || captainMutation.isPending;

  const team = data?.team;
  const players = data?.players ?? [];
  const eligible = data?.eligiblePlayers ?? [];

  const isLockedOrCompleted =
    team?.gameweek.status === "LOCKED" ||
    team?.gameweek.status === "COMPLETED";

  // Index by playerId so "already on team" lookups are O(1)
  const onTeamIds = useMemo(
    () => new Set(players.map((p) => p.playerId)),
    [players],
  );

  /** Sort the XI into FWD → MID → DEF → GK rows for the pitch view. */
  const byPosition = useMemo(() => {
    const buckets: Record<Position, AdminTeamPlayerCard[]> = {
      GK: [],
      DEF: [],
      MID: [],
      FWD: [],
    };
    for (const p of players) {
      if (buckets[p.position]) buckets[p.position].push(p);
    }
    return buckets;
  }, [players]);

  const onSwapConfirmed = () => {
    if (!pendingAction || pendingAction.kind !== "swap") return;
    const { outPlayerId, inPlayerId, inName } = pendingAction;
    swapMutation.mutate(
      { teamId, outPlayerId, inPlayerId },
      {
        onSuccess: () => {
          toast.success(`Swapped in ${inName}`);
          setSwapSlot(null);
          setPendingAction(null);
        },
        onError: (e: unknown) => {
          const msg =
            (e as { response?: { data?: { message?: string } } }).response?.data
              ?.message ?? "Swap failed";
          toast.error(msg);
        },
      },
    );
  };

  const onCaptainConfirmed = () => {
    if (!pendingAction || pendingAction.kind !== "captain") return;
    const { captainPlayerId, name } = pendingAction;
    captainMutation.mutate(
      { teamId, captainPlayerId },
      {
        onSuccess: () => {
          toast.success(`${name} is now captain`);
          setPendingAction(null);
        },
        onError: (e: unknown) => {
          const msg =
            (e as { response?: { data?: { message?: string } } }).response?.data
              ?.message ?? "Captain change failed";
          toast.error(msg);
        },
      },
    );
  };

  const handleSwapPick = (inPlayer: AdminEligiblePlayer) => {
    if (!swapSlot) return;
    const action = {
      kind: "swap" as const,
      outPlayerId: swapSlot.playerId,
      inPlayerId: inPlayer.id,
      inName: inPlayer.name,
    };
    if (isLockedOrCompleted) {
      setPendingAction(action);
    } else {
      // Skip confirmation modal on UPCOMING/OPEN — no recalc to warn about
      swapMutation.mutate(
        { teamId, outPlayerId: action.outPlayerId, inPlayerId: action.inPlayerId },
        {
          onSuccess: () => {
            toast.success(`Swapped in ${action.inName}`);
            setSwapSlot(null);
          },
          onError: (e: unknown) => {
            const msg =
              (e as { response?: { data?: { message?: string } } }).response
                ?.data?.message ?? "Swap failed";
            toast.error(msg);
          },
        },
      );
    }
  };

  const handleCaptainPick = (player: AdminTeamPlayerCard) => {
    if (player.isCaptain) return;
    const action = {
      kind: "captain" as const,
      captainPlayerId: player.playerId,
      name: player.player.name,
    };
    if (isLockedOrCompleted) {
      setPendingAction(action);
    } else {
      captainMutation.mutate(
        { teamId, captainPlayerId: action.captainPlayerId },
        {
          onSuccess: () => toast.success(`${action.name} is now captain`),
          onError: (e: unknown) => {
            const msg =
              (e as { response?: { data?: { message?: string } } }).response
                ?.data?.message ?? "Captain change failed";
            toast.error(msg);
          },
        },
      );
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex justify-end"
      onClick={onClose}
    >
      <div
        className="w-full max-w-5xl bg-white shadow-2xl flex flex-col h-full"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            {team?.user.avatar ? (
              <img
                src={team.user.avatar}
                alt=""
                className="w-10 h-10 rounded-full object-cover"
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white font-bold">
                {team?.user.name.charAt(0).toUpperCase() ?? "?"}
              </div>
            )}
            <div className="min-w-0">
              <div className="text-lg font-bold text-gray-900 truncate">
                {team?.user.name ?? "—"}
                {team?.user.username && (
                  <span className="ml-2 text-sm font-normal text-gray-400">
                    @{team.user.username}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <span className="font-semibold">{team?.gameweek.name}</span>
                {team && (
                  <Badge
                    className={cn(
                      STATUS_COLOR[team.gameweek.status] ?? "bg-gray-100",
                      "border-0",
                    )}
                  >
                    {team.gameweek.status}
                  </Badge>
                )}
                <span>·</span>
                <span>
                  <b className="text-gray-900">{team?.totalPoints ?? 0}</b> pts
                </span>
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setShowAudit((s) => !s)}
            >
              <History className="h-4 w-4 mr-1.5" />
              Audit ({auditData?.actions.length ?? 0})
            </Button>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-700"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* LOCKED warning banner */}
        {isLockedOrCompleted && (
          <div className="px-6 py-2.5 bg-amber-50 border-b border-amber-200 flex items-center gap-2 text-xs text-amber-900">
            <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />
            <span>
              This matchweek is <b>{team?.gameweek.status}</b>. Swaps and
              captain changes will auto-trigger a points recalculation —
              historical leaderboards will update to reflect the new XI.
            </span>
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="flex items-center justify-center h-48 text-gray-500">
              <Loader2 className="h-6 w-6 animate-spin mr-2" />
              Loading XI…
            </div>
          ) : !team ? (
            <div className="text-center text-gray-500">Team not found.</div>
          ) : (
            <div className="space-y-6">
              {POSITION_ROWS.map((row) => {
                const rowPlayers = byPosition[row.key];
                if (rowPlayers.length === 0) return null;
                return (
                  <div key={row.key}>
                    <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                      {row.label}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {rowPlayers.map((p) => (
                        <PlayerCard
                          key={p.id}
                          card={p}
                          isSwapTarget={swapSlot?.id === p.id}
                          onSwapClick={() => setSwapSlot(p)}
                          onCaptainClick={() => handleCaptainPick(p)}
                          isMutating={isMutating}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Audit panel */}
          {showAudit && auditData && (
            <div className="mt-8 border-t pt-6">
              <div className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-3">
                Recent admin actions
              </div>
              {auditData.actions.length === 0 ? (
                <div className="text-sm text-gray-500 italic">
                  No admin actions yet.
                </div>
              ) : (
                <div className="space-y-2">
                  {auditData.actions.map((a) => (
                    <div
                      key={a.id}
                      className="text-xs bg-gray-50 border border-gray-200 rounded p-2.5 flex items-start gap-3"
                    >
                      <div className="font-mono text-gray-400">
                        #{a.id}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-gray-900">
                          {a.action === "SWAP"
                            ? `Swapped player ${a.outPlayerId} → ${a.inPlayerId}`
                            : `Changed captain to player ${a.newCaptainId}`}
                        </div>
                        <div className="text-gray-500 mt-0.5">
                          by <b>{a.admin.name}</b> ·{" "}
                          {new Date(a.createdAt).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Swap panel */}
      {swapSlot && (
        <SwapPanel
          slot={swapSlot}
          eligible={eligible}
          onTeamIds={onTeamIds}
          onPick={handleSwapPick}
          onClose={() => setSwapSlot(null)}
          isMutating={isMutating}
        />
      )}

      {/* Confirmation modal for LOCKED / COMPLETED actions */}
      {pendingAction && (
        <ConfirmModal
          action={pendingAction}
          gameweekName={team?.gameweek.name ?? ""}
          status={team?.gameweek.status ?? ""}
          onCancel={() => setPendingAction(null)}
          onConfirm={() =>
            pendingAction.kind === "swap"
              ? onSwapConfirmed()
              : onCaptainConfirmed()
          }
          isPending={isMutating}
        />
      )}
    </div>
  );
}

// ─── Player card on the XI ─────────────────────────────────────────────

function PlayerCard({
  card,
  isSwapTarget,
  onSwapClick,
  onCaptainClick,
  isMutating,
}: {
  card: AdminTeamPlayerCard;
  isSwapTarget: boolean;
  onSwapClick: () => void;
  onCaptainClick: () => void;
  isMutating: boolean;
}) {
  const photo = card.player.photo;
  const initials = card.player.name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  return (
    <div
      className={cn(
        "border rounded-lg p-3 transition",
        POS_BG[card.position],
        isSwapTarget && "ring-2 ring-emerald-500",
      )}
    >
      <div className="flex items-center gap-3">
        <div className="relative shrink-0">
          <div className="w-12 h-12 rounded-full bg-white border border-gray-200 overflow-hidden flex items-center justify-center">
            {photo ? (
              <img
                src={photo}
                alt={card.player.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <span className="text-xs font-bold text-gray-500">
                {initials}
              </span>
            )}
          </div>
          {card.isCaptain && (
            <div
              className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-amber-400 text-amber-900 text-[10px] font-black flex items-center justify-center border-2 border-white"
              title="Captain"
            >
              C
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold text-gray-900 truncate">
            {card.player.name}
          </div>
          <div className="text-[10px] text-gray-500 truncate flex items-center gap-1">
            {card.player.club?.name && (
              <span>{card.player.club.name}</span>
            )}
            <span className="font-mono text-gray-400">
              · {card.position}
            </span>
            <span className="ml-auto font-bold text-gray-700">
              {card.points} pts
            </span>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 mt-3">
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={onSwapClick}
          disabled={isMutating}
          className="h-7 text-[11px]"
        >
          <ArrowLeftRight className="h-3 w-3 mr-1" />
          Swap
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={onCaptainClick}
          disabled={isMutating || card.isCaptain}
          className={cn(
            "h-7 text-[11px]",
            card.isCaptain && "opacity-50",
          )}
        >
          <Star className="h-3 w-3 mr-1" />
          {card.isCaptain ? "Captain" : "Make C"}
        </Button>
      </div>
    </div>
  );
}

// ─── Swap panel — pick a player to bring in ────────────────────────────

function SwapPanel({
  slot,
  eligible,
  onTeamIds,
  onPick,
  onClose,
  isMutating,
}: {
  slot: AdminTeamPlayerCard;
  eligible: AdminEligiblePlayer[];
  onTeamIds: Set<number>;
  onPick: (p: AdminEligiblePlayer) => void;
  onClose: () => void;
  isMutating: boolean;
}) {
  const [search, setSearch] = useState("");
  const [positionFilter, setPositionFilter] = useState<Position | "ANY">(
    slot.position,
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return eligible.filter((p) => {
      if (positionFilter !== "ANY" && p.position !== positionFilter)
        return false;
      if (!q) return true;
      return (
        p.name.toLowerCase().includes(q) ||
        p.club?.name.toLowerCase().includes(q) ||
        p.nation?.name.toLowerCase().includes(q)
      );
    });
  }, [eligible, positionFilter, search]);

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b flex items-center justify-between">
          <div>
            <div className="text-sm text-gray-500">
              Swap out:{" "}
              <span className="font-bold text-gray-900">
                {slot.player.name}
              </span>{" "}
              <span className="text-gray-400">({slot.position})</span>
            </div>
            <div className="text-xs text-gray-500 mt-0.5">
              Pick a replacement.
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-5 py-3 border-b grid grid-cols-3 gap-2">
          <div className="col-span-2 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
            <Input
              placeholder="Search player / club / nation…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 h-9 text-sm"
            />
          </div>
          <div className="flex gap-1">
            {(["GK", "DEF", "MID", "FWD", "ANY"] as const).map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setPositionFilter(p)}
                className={cn(
                  "flex-1 text-[10px] font-bold py-1.5 rounded border transition",
                  positionFilter === p
                    ? "border-emerald-600 bg-emerald-50 text-emerald-700"
                    : "border-gray-200 text-gray-600 hover:bg-gray-50",
                )}
              >
                {p}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-3">
          {filtered.length === 0 ? (
            <div className="text-center text-sm text-gray-500 py-12">
              No players match.
            </div>
          ) : (
            <div className="space-y-1.5">
              {filtered.slice(0, 200).map((p) => {
                const onTeam = onTeamIds.has(p.id);
                return (
                  <button
                    key={p.id}
                    type="button"
                    disabled={onTeam || isMutating}
                    onClick={() => onPick(p)}
                    className={cn(
                      "w-full flex items-center gap-3 p-2 rounded border text-left transition",
                      onTeam
                        ? "border-gray-100 bg-gray-50 opacity-60 cursor-not-allowed"
                        : "border-gray-200 hover:border-emerald-500 hover:bg-emerald-50",
                    )}
                  >
                    <div className="w-9 h-9 rounded-full bg-gray-100 border overflow-hidden flex items-center justify-center shrink-0">
                      {p.photo ? (
                        <img
                          src={p.photo}
                          alt={p.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <span className="text-[10px] font-bold text-gray-500">
                          {p.name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase()}
                        </span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold text-gray-900 truncate">
                        {p.name}
                      </div>
                      <div className="text-[10px] text-gray-500 truncate">
                        {p.club?.name ?? "—"} · {p.nation?.name ?? "—"}
                      </div>
                    </div>
                    <div className="text-[10px] font-mono font-bold text-gray-400">
                      {p.position}
                    </div>
                    {onTeam && (
                      <Badge className="bg-gray-200 text-gray-700 border-0 text-[10px]">
                        on team
                      </Badge>
                    )}
                  </button>
                );
              })}
              {filtered.length > 200 && (
                <div className="text-xs text-gray-400 text-center py-2">
                  Showing first 200 of {filtered.length} — refine the search.
                </div>
              )}
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t flex justify-end">
          <Button variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Confirmation modal for LOCKED / COMPLETED actions ─────────────────

function ConfirmModal({
  action,
  gameweekName,
  status,
  onCancel,
  onConfirm,
  isPending,
}: {
  action:
    | { kind: "swap"; inName: string }
    | { kind: "captain"; name: string };
  gameweekName: string;
  status: string;
  onCancel: () => void;
  onConfirm: () => void;
  isPending: boolean;
}) {
  return (
    <div
      className="fixed inset-0 z-[70] bg-black/60 flex items-center justify-center p-4"
      onClick={onCancel}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-md p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 mb-3">
          <AlertTriangle className="h-5 w-5 text-amber-600" />
          <div className="text-base font-bold text-gray-900">
            {gameweekName} is {status}
          </div>
        </div>
        <p className="text-sm text-gray-600 mb-4 leading-relaxed">
          {action.kind === "swap" ? (
            <>
              Swapping in <b>{action.inName}</b> on a {status.toLowerCase()}{" "}
              matchweek will <b>re-trigger the points recalculation</b> for
              this gameweek. The user's leaderboard position may change.
            </>
          ) : (
            <>
              Making <b>{action.name}</b> captain on a {status.toLowerCase()}{" "}
              matchweek will <b>re-trigger the points recalculation</b> for
              this gameweek (captain doubles points). The user's leaderboard
              position may change.
            </>
          )}
        </p>
        <p className="text-xs text-gray-500 mb-4">
          The change will be logged in the audit trail.
        </p>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onCancel} disabled={isPending}>
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={isPending}
            className="bg-amber-600 hover:bg-amber-700 text-white"
          >
            {isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Yes, apply change
          </Button>
        </div>
      </div>
    </div>
  );
}
